import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  Text,
  Spinner,
  Button,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../components/Common/ScrollableMessageBar';
import {
  ChevronLeftRegular } from
'@fluentui/react-icons';
import { Step1PolicyCreation } from '../components/Wizard/Step1PolicyCreation';
import { policyService, CreatePolicyRequest } from '../services/policyService';
import { useUnifiedApp } from '../contexts/UnifiedAppContext';
import useEventTracker from '../hooks/useEventTracker';
import { ElementButton, PageName } from '../tracking/tracking.types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px')
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderBottom('3px', 'solid', '#0047AB'),
    ...shorthands.padding('0', '0', '20px', '0'),
    ...shorthands.margin('0', '0', '24px', '0')
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center'
  },
  backButton: {
    minWidth: 'auto',
    ...shorthands.padding('4px', '8px'),
    marginRight: '12px'
  },
  title: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#1a1a1a',
    letterSpacing: '-0.5px'
  },
  content: {
    backgroundColor: '#ffffff',
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('24px'),
    minHeight: '500px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('24px'),
    ...shorthands.padding('48px')
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap('24px'),
    ...shorthands.padding('48px')
  },
  buttonGroup: {
    display: 'flex',
    ...shorthands.gap('12px')
  }
});

export const PolicyCreation: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);
  const { refreshData } = useUnifiedApp();
  const eventTracker = useEventTracker();

  const [isCreatingPolicy, setIsCreatingPolicy] = useState(false);
  const [policyCreationError, setPolicyCreationError] = useState<string | null>(null);


  const handlePolicySubmit = async (step1Data: any) => {
    setIsCreatingPolicy(true);
    setPolicyCreationError(null);

    try {
      console.log('📥 handlePolicySubmit received step1Data:', JSON.stringify(step1Data, null, 2));

      // Extract groups and users from selectedEntities
      const selectedEntities = step1Data.selectedEntities || [];
      const groups = selectedEntities.filter((entity: any) => entity.type === 'group');
      const users = selectedEntities.filter((entity: any) => entity.type === 'user');

      console.log('🔄 Processing entities for policy creation:', {
        totalEntities: selectedEntities.length,
        groups: groups.length,
        users: users.length,
        entities: selectedEntities.map((e: any) => `${e.displayName} (${e.type})`),
        userDetails: users.map((u: any) => ({
          id: u.id,
          displayName: u.displayName,
          userPrincipalName: u.userPrincipalName,
          mail: u.mail,
          type: u.type
        }))
      });

      // Use first group for legacy compatibility
      const primaryGroup = groups[0] || step1Data.selectedGroup;

      // Standard mode - using the correct CreatePolicyRequest interface
      const policyRequest: CreatePolicyRequest = {
        name: step1Data.policyName,
        userGroup: primaryGroup?.displayName || step1Data.securityGroup || '',
        entraGroupId: primaryGroup?.id, // Legacy single group support
        groupDisplayName: primaryGroup?.displayName, // Legacy single group display name
        groups: groups.map((group: any) => ({
          groupId: group.id,
          displayName: group.displayName,
          rank: 10
        })),
        users: users.map((user: any) => ({
          userId: user.id,
          userEmail: user.userPrincipalName || user.mail || user.id,
          displayName: user.displayName
        })),
        bilateralChat: step1Data.bilateralChat,
        enableFederationAccess: step1Data.enableFederationAccess,
        managementMode: 'Actively Managed',
        customDomains: step1Data.includedDomains || step1Data.allowedDomains || []
      };

      // Skip PowerShell preview and create policy directly

      // If preview failed or was skipped, create directly
      await policyService.createPolicy(policyRequest);

      // A stale sidebar count must not hide a successfully created policy.
      try {
        await refreshData();
      } catch (refreshError) {
        console.warn('Failed to refresh policy counts after creation:', refreshError);
      }

      navigate('/policies', { state: { refresh: true, createdPolicy: policyRequest.name } });
    } catch (error) {
      console.error('Failed to create policy:', error);
      setPolicyCreationError(error instanceof Error ? error.message : 'Failed to create policy');
      dispatchToast(
        <Toast data-testid="policy-creation-toast-failed">
          <ToastTitle data-testid="policy-creation-failed-toast-title">Policy creation failed</ToastTitle>
          <ToastBody data-testid="policy-creation-toast-body">{error instanceof Error ? error.message : 'Failed to create policy'}</ToastBody>
        </Toast>,
        { intent: "error" }
      );
    } finally {
      setIsCreatingPolicy(false);
    }
  };

  const handleBack = () => {
    eventTracker.eventTrackOnElementClicked({
      pageName: PageName.ADD_POLICY,
      elementName: ElementButton.BACK,
      elementType: 'button'
    });
    navigate('/policies');
  };


  const renderContent = () => {
    // Show loading state during policy creation
    if (isCreatingPolicy) {
      return (
        <div className={styles.loadingContainer} data-testid="policy-creation-loading-container-div">
          <Spinner size="large" data-testid="policy-creation-spinner" />
          <Text style={{ fontSize: '18px', fontWeight: '600' }} data-testid="policy-creation-creating-your-teams-policy-text">
            Creating your Teams policy...
          </Text>
          <Text style={{ fontSize: '14px', color: '#605e5c', textAlign: 'center' }} data-testid="policy-creation-this-may-take-a-few-moments-text">
            This may take a few moments as we configure your Teams External Collaboration policy
            and validate it against the Teams Admin Center.
          </Text>
        </div>);

    }

    // Show error state if policy creation failed
    if (policyCreationError) {
      return (
        <div className={styles.errorContainer} data-testid="policy-creation-error-container-div">
          <MessageBar intent="error" data-testid="policy-creation-message-bar">
            <MessageBarBody data-testid="policy-creation-message-bar-body">
              <Text style={{ fontWeight: '600' }} data-testid="policy-creation-failed-text">Policy Creation Failed</Text>
              <br data-testid="policy-creation-error-line-break-br" />
              <Text data-testid="policy-creation-text">{policyCreationError}</Text>
            </MessageBarBody>
          </MessageBar>
          <div className={styles.buttonGroup} data-testid="policy-creation-button-group-div">
            <Button
              appearance="primary"
              onClick={() => {
                setPolicyCreationError(null);
              }} data-testid="policy-creation-try-again-button">

              Try Again
            </Button>
            <Button
              appearance="secondary"
            onClick={handleBack} data-testid="policy-creation-return-to-dashboard-button">

              Return to Dashboard
            </Button>
          </div>
        </div>);

    }

    // Show the policy creation form (Step1)
    return (
      <Step1PolicyCreation
        onNext={handlePolicySubmit}
        onBack={handleBack}
        isGFCMode={false}
        gfcReady={false} data-testid="policy-creation-step1-policy-creation" />);


  };

  return (
    <div className={styles.container} data-testid="policy-creation-container-div">
      <Toaster toasterId={toasterId} position="top-end" pauseOnHover pauseOnWindowBlur data-testid="policy-creation-toaster" />
      <div className={styles.header} data-testid="policy-creation-header-div">
        <div className={styles.headerContent} data-testid="policy-creation-header-content-div">
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular data-testid="policy-creation-chevron-left-regular" />}
            className={styles.backButton}
            onClick={handleBack} data-testid="policy-creation-back-button">

            Back
          </Button>
          <Text className={styles.title} data-testid="policy-creation-add-policy-text">Add Policy</Text>
        </div>
      </div>

      <div className={styles.content} data-testid="policy-creation-content-div">
        {renderContent()}
      </div>
      
    </div>);

};